class HeroSlideshowComponent extends HTMLElement {
  constructor() {
    super();

    this._applyStackedLayouts();

    this.slides = this.querySelectorAll('.hero-slideshow__slide');
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.autorotate = this.dataset.autorotate === 'true';
    this.autorotateSpeed = parseInt(this.dataset.autorotateSpeed) || 5000;
    this.autorotateTimer = null;
    this.touchStartX = 0;
    this.isDragging = false;

    // Activate first slide and mark component as JS-ready
    if (this.slides[0]) this.slides[0].classList.add('is-active');
    this.setAttribute('data-ready', '');

    if (this.totalSlides > 1) {
      this._buildNav();
      this.dots = this.querySelectorAll('.hero-slideshow__dot');
      if (this.dots[0]) {
        this.dots[0].classList.add('is-active');
        this.dots[0].setAttribute('aria-selected', 'true');
      }
      this.bindDots();
      this.bindTouch();
      this.bindMouse();
      if (this.autorotate) this.startAutorotate();
    }

    this._bindSlideLinks();

    document.addEventListener('shopify:section:load', () => this._applyStackedLayouts());
  }

  _bindSlideLinks() {
    this.slides.forEach(slide => {
      const url = slide.dataset.slideUrl;
      if (!url) return;
      slide.addEventListener('click', (e) => {
        if (e.target.closest('a, button')) return;
        window.location.href = url;
      });
    });
  }

  _applyStackedLayouts() {
    this.querySelectorAll('.hero-slideshow__slide').forEach(slide => {
      const inner = slide.querySelector('.hero-slideshow__content-inner');
      if (!inner) return;
      const layout = inner.dataset.uspLayout;
      if (layout !== 'stacked' && layout !== 'two-column') return;

      const logoItems = [...inner.children].filter(el =>
        el.classList.contains('hero-slideshow__logo') || el.querySelector('.hero-slideshow__logo')
      );
      const uspItems = [...inner.children].filter(el =>
        el.classList.contains('hero-slideshow__usp') || el.querySelector('.hero-slideshow__usp')
      );
      if (logoItems.length === 0 && uspItems.length === 0) return;

      const columns = document.createElement('div');
      columns.className = 'hero-slideshow__columns';

      if (logoItems.length > 0) {
        const logoGroup = document.createElement('div');
        logoGroup.className = 'hero-slideshow__logo-group';
        logoItems.forEach(el => logoGroup.appendChild(el));
        columns.appendChild(logoGroup);
      }

      const uspGroup = document.createElement('div');
      uspGroup.className = 'hero-slideshow__usp-group';
      uspItems.forEach(el => uspGroup.appendChild(el));
      columns.appendChild(uspGroup);

      inner.appendChild(columns);
    });
  }

  _buildNav() {
    const nav = document.createElement('div');
    nav.className = 'hero-slideshow__nav';
    nav.setAttribute('aria-label', 'Slideshow navigation');

    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement('button');
      dot.className = 'hero-slideshow__dot';
      dot.dataset.slide = i;
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('aria-selected', 'false');
      nav.appendChild(dot);
    }

    this.appendChild(nav);
  }

  goTo(index) {
    const prev = this.currentIndex;
    this.currentIndex = ((index % this.totalSlides) + this.totalSlides) % this.totalSlides;
    if (prev === this.currentIndex) return;

    this.slides[prev].classList.remove('is-active');
    if (this.dots[prev]) {
      this.dots[prev].classList.remove('is-active');
      this.dots[prev].setAttribute('aria-selected', 'false');
    }

    this.slides[this.currentIndex].classList.add('is-active');
    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.add('is-active');
      this.dots[this.currentIndex].setAttribute('aria-selected', 'true');
    }
  }

  next() { this.goTo(this.currentIndex + 1); }
  previous() { this.goTo(this.currentIndex - 1); }

  bindDots() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.stopAutorotate();
        this.goTo(index);
      });
    });
  }

  bindMouse() {
    let startX = 0;
    let dragged = false;
    let navigated = false;

    const onPointerMove = (e) => {
      if (Math.abs(e.clientX - startX) > 5) {
        dragged = true;
        this.classList.add('is-dragging');
      }
    };

    const onPointerUp = (e) => {
      this.removeEventListener('pointermove', onPointerMove);
      this.removeEventListener('pointerup', onPointerUp);
      this.removeEventListener('pointercancel', onPointerUp);
      this.classList.remove('is-dragging');
      const diff = startX - e.clientX;
      if (dragged && Math.abs(diff) > 40) {
        this.stopAutorotate();
        diff > 0 ? this.next() : this.previous();
        navigated = true;
      }
    };

    // Use capture phase so we get pointer events before the slide link (which can interfere with drag)
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      // Don't capture interactive elements — setPointerCapture redirects click to the
      // component instead of the original target, breaking buttons and nav dots
      if (e.target.closest('button, a')) return;
      startX = e.clientX;
      dragged = false;
      this.setPointerCapture(e.pointerId);
      this.addEventListener('pointermove', onPointerMove);
      this.addEventListener('pointerup', onPointerUp);
      this.addEventListener('pointercancel', onPointerUp);
    }, true);

    // Suppress click on links/buttons only if a swipe navigation actually occurred
    this.addEventListener('click', (e) => {
      if (navigated) {
        e.preventDefault();
        e.stopPropagation();
        navigated = false;
      }
      dragged = false;
    }, true);
  }

  bindTouch() {
    this.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.isDragging = true;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      const diff = this.touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        this.stopAutorotate();
        diff > 0 ? this.next() : this.previous();
      }
    }, { passive: true });
  }

  startAutorotate() {
    this.stopAutorotate();
    this.autorotateTimer = setInterval(() => this.next(), this.autorotateSpeed);
  }

  stopAutorotate() {
    if (this.autorotateTimer) {
      clearInterval(this.autorotateTimer);
      this.autorotateTimer = null;
    }
  }
}

customElements.define('hero-slideshow-component', HeroSlideshowComponent);
